import * as React from "react";

import { Progress } from "./progress";
import { cn } from "./utils";

export type FlowPhaseStage = "past" | "current" | "future";

export interface FlowPhase {
    /** Short label shown beneath the bar, e.g. "Consent". */
    label: string;
    /** Optional label for the header row (defaults to `label`). */
    headerLabel?: string;
    /** Optional short substage label shown under the mini-bar (e.g. "Name"). */
    subLabel?: string;
    /** Total number of steps within this phase. */
    totalSteps: number;
    /** Steps completed within this phase. */
    completedSteps: number;
    /**
     * Optional explicit stage. When omitted it is derived: a phase is "past"
     * once every step is complete, the first not-yet-complete phase is
     * "current", and the rest are "future".
     */
    stage?: FlowPhaseStage;
}

interface Props {
    phases: FlowPhase[];
    /** Hide the "Phase · Step X of Y" header line above the bars. */
    hideHeader?: boolean;
    className?: string;
}

const clampPercent = (completed: number, total: number) => {
    if (total <= 0) return 0;
    return Math.max(0, Math.min(100, Math.round((completed / total) * 100)));
};

/** Resolve each phase's stage, deriving from progress when not supplied. */
const resolveStages = (phases: FlowPhase[]): FlowPhaseStage[] => {
    let currentAssigned = false;
    return phases.map((phase) => {
        if (phase.stage) {
            if (phase.stage === "current") currentAssigned = true;
            return phase.stage;
        }
        const isComplete = phase.completedSteps >= phase.totalSteps && phase.totalSteps > 0;
        if (isComplete) return "past";
        if (!currentAssigned) {
            currentAssigned = true;
            return "current";
        }
        return "future";
    });
};

const labelColor = (stage: FlowPhaseStage) =>
    cn(
        stage === "past" && "text-indigo-600 dark:text-indigo-400",
        stage === "current" && "text-violet-700 dark:text-violet-300",
        stage === "future" && "text-slate-400 dark:text-slate-500"
    );

/**
 * Compact, persistent in-flow progress header for a multi-phase journey
 * (e.g. Onboarding → Setup → Consent → Forms).
 *
 * Each phase renders its own mini {@link Progress} bar that fills as the
 * patient completes its steps, with a leading "Phase · Step X of Y" line so
 * they always know where they are and how much of the current phase remains.
 */
export const FlowProgress = ({ phases, hideHeader = false, className }: Props) => {
    if (!phases.length) return null;

    const stages = resolveStages(phases);
    const currentIndex = stages.indexOf("current");
    const current = currentIndex >= 0 ? phases[currentIndex] : undefined;

    return (
        <div className={cn("flex w-full flex-col gap-2", className)} data-testid="flow-progress">
            {!hideHeader && current && (
                <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-slate-900 dark:text-slate-100">
                        {current.headerLabel ?? current.label}
                    </span>
                    <span className="font-medium tabular-nums text-muted-foreground">
                        Step {Math.min(current.completedSteps + 1, current.totalSteps)} of {current.totalSteps}
                    </span>
                </div>
            )}

            <div className="flex items-center gap-2">
                {phases.map((phase, index) => (
                    <Progress
                        key={`${phase.label}-${index}`}
                        value={clampPercent(phase.completedSteps, phase.totalSteps)}
                        className="h-2 flex-1 bg-indigo-100 dark:bg-indigo-950"
                        aria-label={`${phase.label}: ${phase.completedSteps} of ${phase.totalSteps} steps complete`}
                    />
                ))}
            </div>

            <div className="flex gap-2 text-[11px] font-medium">
                {phases.map((phase, index) => (
                    <span
                        key={`${phase.label}-${index}-label`}
                        className={cn("flex-1 text-left", labelColor(stages[index]))}
                    >
                        {phase.subLabel ?? phase.label}
                    </span>
                ))}
            </div>
        </div>
    );
};

export default FlowProgress;
